import { useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { signInWithEmailAndPassword, GoogleAuthProvider, signInWithPopup } from 'firebase/auth'
import { doc, setDoc, getDoc } from 'firebase/firestore'
import { auth, db } from '@/config/firebase'
import { useAuth } from '../hooks/useAuth'
import { Button } from '@/components/Button'
import toast from 'react-hot-toast'
import { useTranslation } from '@/i18n'

export function LoginPage() {
  const { user } = useAuth()
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)

  if (user) {
    return <Navigate to="/" replace />
  }

  const createUserDocumentIfNeeded = async (user: any) => {
    // Check if user document already exists
    const userDocRef = doc(db, 'users', user.uid)
    const userDoc = await getDoc(userDocRef)
    
    if (!userDoc.exists()) {
      // Create user document for first-time Google sign-in
      await setDoc(userDocRef, {
        displayName: user.displayName || 'User',
        email: user.email,
        role: 'user',
        status: 'active',
        locale: navigator.language.startsWith('fr') ? 'fr' : 'en',
        settings: {
          notifications: true,
          analytics: true,
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      })
      console.log('✅ Created user document for:', user.email)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      await signInWithEmailAndPassword(auth, email, password)
      toast.success(t('auth.welcomeBack'))
    } catch (error: any) {
      console.error('Login error:', error)
      toast.error(getErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true)
    try {
      const provider = new GoogleAuthProvider()
      // Add additional scopes if needed
      provider.addScope('email')
      provider.addScope('profile')
      
      const result = await signInWithPopup(auth, provider)
      
      // Create user document if it doesn't exist (for first-time sign-in)
      await createUserDocumentIfNeeded(result.user)
      
      toast.success(t('auth.welcomeBack'))
    } catch (error: any) {
      console.error('Google sign-in error:', error)
      
      // Special handling for configuration errors
      if (error.code === 'auth/configuration-not-found') {
        toast.error(t('auth.googleSignInNotConfiguredMessage'))
        console.error('🔥 Google Sign-In Configuration Issue:', {
          error: error.message,
          code: error.code,
          projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
          troubleshooting: 'Please enable Google Sign-In in Firebase Console > Authentication > Sign-in method'
        })
      } else {
        toast.error(getErrorMessage(error))
      }
    } finally {
      setGoogleLoading(false)
    }
  }

  const getErrorMessage = (error: any) => {
    switch (error.code) {
      case 'auth/user-not-found':
        return t('errors.noAccountFound')
      case 'auth/wrong-password':
        return t('errors.incorrectPassword')
      case 'auth/invalid-email':
        return t('errors.invalidEmail')
      case 'auth/user-disabled':
        return t('errors.accountDisabled')
      case 'auth/too-many-requests':
        return t('errors.tooManyAttempts')
      case 'auth/popup-closed-by-user':
        return t('errors.signInCancelled')
      case 'auth/configuration-not-found':
        return t('errors.googleSignInNotConfigured')
      case 'auth/operation-not-allowed':
        return t('errors.signInMethodNotEnabled')
      case 'auth/popup-blocked':
        return t('errors.popupBlocked')
      case 'auth/cancelled-popup-request':
        return t('errors.signInCancelled')
      default:
        return error.message || t('errors.failedToSignIn')
    }
  }



  return (
    <div className="min-h-screen bg-bg">
      <div className="flex items-center justify-center px-lg py-3xl">
        <div className="w-full max-w-md lg:max-w-4xl">
          <div className="bg-surface rounded-xl border border-border p-2xl lg:p-3xl">
            {/* Header */}
            <div className="text-center mb-2xl lg:mb-3xl">
              <h1 className="text-2xl lg:text-3xl font-semibold text-text mb-sm">
                {t('auth.login')}
              </h1>
              <p className="text-text-muted">
                {t('auth.signInToContinue')}
              </p>
            </div>

            {/* Mobile Layout */}
            <div className="lg:hidden">
              {/* Mobile Form */}
              <form onSubmit={handleSubmit} className="space-y-lg">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-text mb-sm">
                    {t('auth.email')}
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full px-lg py-md border border-border rounded-full bg-bg text-text placeholder-text-faint focus:outline-none focus:ring-2 focus:ring-focus transition-all"
                    placeholder={t('auth.enterYourEmail')}
                  />
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-text mb-sm">
                    {t('auth.password')}
                  </label>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="w-full px-lg py-md border border-border rounded-full bg-bg text-text placeholder-text-faint focus:outline-none focus:ring-2 focus:ring-focus transition-all"
                    placeholder={t('auth.enterYourPassword')}
                  />
                </div>

                <Button
                  type="submit"
                  loading={loading}
                  disabled={loading || googleLoading}
                  className="w-full"
                >
                  {t('auth.signIn')}
                </Button>
              </form>

              {/* Mobile Divider */}
              <div className="my-lg">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-border" />
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-sm bg-surface text-text-muted">
                      {t('auth.orContinueWith')}
                    </span>
                  </div>
                </div>
              </div>

              {/* Mobile Social Login */}
              <Button
                type="button"
                variant="secondary"
                onClick={handleGoogleSignIn}
                loading={googleLoading}
                disabled={loading || googleLoading}
                className="w-full"
              >
                <svg className="w-5 h-5 mr-sm" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {t('auth.signInWithGoogle')}
              </Button>
            </div>

            {/* Desktop Layout */}
            <div className="hidden lg:grid lg:items-center lg:gap-xl" style={{ gridTemplateColumns: '1fr auto 1fr' }}>
              {/* Left side - Form */}
              <div>
                <form onSubmit={handleSubmit} className="space-y-lg">
                  <div>
                    <label htmlFor="email-desktop" className="block text-sm font-medium text-text mb-sm">
                      {t('auth.email')}
                    </label>
                    <input
                      id="email-desktop"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="w-full px-lg py-md border border-border rounded-full bg-bg text-text placeholder-text-faint focus:outline-none focus:ring-2 focus:ring-focus transition-all"
                      placeholder={t('auth.enterYourEmail')}
                    />
                  </div>

                  <div>
                    <label htmlFor="password-desktop" className="block text-sm font-medium text-text mb-sm">
                      {t('auth.password')}
                    </label>
                    <input
                      id="password-desktop"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="w-full px-lg py-md border border-border rounded-full bg-bg text-text placeholder-text-faint focus:outline-none focus:ring-2 focus:ring-focus transition-all"
                      placeholder={t('auth.enterYourPassword')}
                    />
                  </div>

                  <Button
                    type="submit"
                    loading={loading}
                    disabled={loading || googleLoading}
                    className="w-full"
                  >
                    {t('auth.signIn')}
                  </Button>
                </form>
              </div>

              {/* Desktop Divider */}
              <div className="flex items-center justify-center px-xl">
                <div className="flex flex-col items-center">
                  <div className="w-px bg-border h-20"></div>
                  <span className="py-md text-sm text-text-muted bg-surface">
                    {t('common.or')}
                  </span>
                  <div className="w-px bg-border h-20"></div>
                </div>
              </div>

              {/* Right side - Social Login */}
              <div className="flex items-center justify-center">
                <div className="w-full">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={handleGoogleSignIn}
                    loading={googleLoading}
                    disabled={loading || googleLoading}
                    className="w-full"
                  >
                    <svg className="w-5 h-5 mr-sm" viewBox="0 0 24 24">
                      <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    {t('auth.signInWithGoogle')}
                  </Button>
                </div>
              </div>
            </div>

            {/* Footer */}
            <p className="mt-xl text-center text-sm text-text-muted">
              {t('auth.dontHaveAccount')}&nbsp;
              <Link to="/register" className="text-primary hover:underline font-medium">
                {t('auth.signUp')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}